import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { Toaster } from './components/ui/sonner';
import { loadPlausible } from './lib/analytics';
import Header from './components/Header';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import Homepage from './pages/Homepage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import OrganiserDashboard from './pages/OrganiserDashboard';
import ScannerPage from './pages/ScannerPage';
import MyTicketsPage from './pages/MyTicketsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import VerifyTicketPage from './pages/VerifyTicketPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import DeleteAccountConfirmPage from './pages/DeleteAccountConfirmPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import AssociateEventsPage from './pages/AssociateEventsPage';
import AssociateToolsPage from './pages/AssociateToolsPage';
import OrganiserTermsPage from './pages/OrganiserTermsPage';
import PayoutPolicyPage from './pages/PayoutPolicyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import AdminPayoutsPage from './pages/AdminPayoutsPage';
import AdminRefundsPage from './pages/AdminRefundsPage';
import AdminAuditLogPage from './pages/AdminAuditLogPage';
import CustomerProvideBankDetailsPage from './pages/CustomerProvideBankDetailsPage';
import ScrollToTop from './components/ScrollToTop';
import AdminDiagnosticsPage from './pages/AdminDiagnosticsPage';
import FeesPage from './pages/FeesPage';
import AboutPage from './pages/AboutPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import FAQPage from './pages/FAQPage';
import OrganiserPublicPage from './pages/OrganiserPublicPage';
import AdminEventsPage from './pages/AdminEventsPage';
import CookieConsent from './components/CookieConsent';
import './App.css';

function App() {
  useEffect(() => {
    // Load Plausible if user has previously granted analytics consent.
    // CookieConsent.js calls loadPlausible() the moment they accept.
    loadPlausible();
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <div className="App scroll-smooth">
            <Header />
            <Routes>
              <Route path="/" element={<Homepage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/:eventId" element={<EventDetailPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requiredRole="organiser">
                    <OrganiserDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/scanner"
                element={
                  <ProtectedRoute requiredRole="organiser">
                    <ScannerPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/my-tickets" element={<MyTicketsPage />} />
              <Route path="/verify/:code" element={<VerifyTicketPage />} />
              <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
              <Route path="/organiser-terms" element={<OrganiserTermsPage />} />
              <Route path="/payout-policy" element={<PayoutPolicyPage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
              <Route path="/delete-account/:token" element={<DeleteAccountConfirmPage />} />
              <Route path="/team/accept/:token" element={<AcceptInvitePage />} />
              <Route
                path="/associate/events"
                element={
                  <ProtectedRoute>
                    <AssociateEventsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/associate/events/:eventId/tools"
                element={
                  <ProtectedRoute>
                    <AssociateToolsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/associate/events/:eventId/scanner"
                element={
                  <ProtectedRoute>
                    <ScannerPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/admin/payouts" element={<AdminPayoutsPage />} />
              <Route path="/admin/events" element={<AdminEventsPage />} />
              <Route path="/admin/refunds" element={<AdminRefundsPage />} />
              <Route path="/admin/audit-log" element={<AdminAuditLogPage />} />
              <Route
                path="/refund-requests/:refundId/bank-details"
                element={
                  <ProtectedRoute>
                    <CustomerProvideBankDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/admin/diagnostics" element={<AdminDiagnosticsPage />} />
              <Route path="/fees" element={<FeesPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/organiser/:slug" element={<OrganiserPublicPage />} />
              <Route path="/organisers/:slug" element={<OrganiserPublicPage />} />
            </Routes>
            <Footer />
            <Toaster position="top-right" />
            <CookieConsent />
          </div>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
